import { VariantSpecificationsEditor } from "./VariantSpecifications";
import { BroadbandFactsEditor } from "./BroadbandFactsEditor";
import React, { useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Save, Plus, Trash2 } from "lucide-react";
import { FieldHelpTooltip } from "./leaseFieldHelp";

import type {
  CatalogEditorViewModel,
  CommerceSummaryMetricViewModel,
  ProductEditorViewModel,
  VariantOfferEditorViewModel,
  VariantEditorViewModel,
} from "../../contracts/commerce-admin";
import {
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  PropertyGrid,
  PropertyItem,
  Surface,
  VerticalStack,
} from "../blocks";

function EditorChrome({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl sm:max-w-2xl animate-in slide-in-from-right duration-300"
      >
        <PaneHeader title={title} subtitle={subtitle} onClear={onClose} />
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <VerticalStack gap={8}>{children}</VerticalStack>
        </div>
        <PaneFooter>{footer}</PaneFooter>
      </div>
    </>,
    document.body,
  );
}

function Field({
  label,
  error,
  children,
  helpText,
}: {
  label: string;
  error?: string | null;
  children: React.ReactNode;
  helpText?: string;
}) {
  return (
    <VerticalStack gap={2}>
      <div className="flex items-center gap-1.5">
        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
          {label}
        </label>
        {helpText ? <FieldHelpTooltip label={label} text={helpText} /> : null}
      </div>
      {children}
      {error ? <p className="text-[10px] font-bold text-destructive">{error}</p> : null}
    </VerticalStack>
  );
}

function AdvancedSection({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-border/60">
      <button
        type="button"
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <span>Advanced settings</span>
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      {open ? (
        <div className="border-t border-border/60 px-4 py-4">
          <VerticalStack gap={6}>{children}</VerticalStack>
        </div>
      ) : null}
    </div>
  );
}

function inputClassName() {
  return "w-full rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm font-medium text-foreground outline-none transition-all focus:border-primary/40 focus:bg-background";
}

function createClassificationEntry() {
  return {
    id: `classification-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    key: "",
    value: "",
    sortOrder: 0,
  };
}

function EditorFooter({
  saveLabel,
  saving,
  onCancel,
  onSave,
}: {
  saveLabel: string;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  return (
    <HorizontalStack gap={2} className="justify-end">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md border border-border/60 px-4 py-2 text-xs font-bold text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="rounded-md bg-primary px-4 py-2 text-xs font-bold text-primary-foreground shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <HorizontalStack gap={2}>
          <Save className="h-3.5 w-3.5" />
          <span>{saveLabel}</span>
        </HorizontalStack>
      </button>
    </HorizontalStack>
  );
}

function MetricsSurface({ metrics, cols }: { metrics?: CommerceSummaryMetricViewModel[]; cols: 2 | 3 }) {
  if (!metrics || metrics.length === 0) {
    return null;
  }
  return (
    <Surface className="border-border/60 p-4">
      <PropertyGrid cols={cols}>
        {metrics.map(metric => (
          <PropertyItem key={metric.id} label={metric.label} value={metric.value} mono={false} />
        ))}
      </PropertyGrid>
    </Surface>
  );
}

export function CatalogEditorSheet({ editor }: { editor: CatalogEditorViewModel }) {
  if (!editor.isOpen) {
    return null;
  }

  return (
    <EditorChrome
      title={editor.title}
      subtitle={editor.subtitle}
      onClose={editor.onClose}
      footer={
        <EditorFooter
          saveLabel={editor.saveActionLabel}
          saving={editor.state === "saving"}
          onCancel={editor.onClose}
          onSave={() => void editor.onSave()}
        />
      }
    >
      <MetricsSurface metrics={editor.metrics} cols={2} />

      <PaneSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Catalog Key" error={editor.validationErrors.catalogKey}>
            <input
              value={editor.value.identity.catalogKey}
              onChange={event =>
                editor.onChange({
                  identity: {
                    ...editor.value.identity,
                    catalogKey: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
          <Field label="Catalog Name" error={editor.validationErrors.name}>
            <input
              value={editor.value.identity.name}
              onChange={event =>
                editor.onChange({
                  identity: {
                    ...editor.value.identity,
                    name: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
        </div>
      </PaneSection>

      <PaneSection label="Lifecycle">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Status">
            <select
              value={editor.value.lifecycle.status}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    status: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Default Currency">
            <select
              value={editor.value.lifecycle.defaultCurrency || ""}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    defaultCurrency: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.currencyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </PaneSection>

      <PaneSection label="Description">
        <Field label="Catalog Description">
          <textarea
            value={editor.value.description || ""}
            onChange={event => editor.onChange({ description: event.target.value })}
            rows={5}
            className={inputClassName()}
          />
        </Field>
      </PaneSection>
    </EditorChrome>
  );
}

export function ProductEditorSheet({ editor }: { editor: ProductEditorViewModel }) {
  if (!editor.isOpen) {
    return null;
  }

  return (
    <EditorChrome
      title={editor.title}
      subtitle={editor.subtitle}
      onClose={editor.onClose}
      footer={
        <EditorFooter
          saveLabel={editor.saveActionLabel}
          saving={editor.state === "saving"}
          onCancel={editor.onClose}
          onSave={() => void editor.onSave()}
        />
      }
    >
      <MetricsSurface metrics={editor.metrics} cols={3} />

      <PaneSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Product Name" error={editor.validationErrors.name}>
            <input
              value={editor.value.identity.name}
              onChange={event =>
                editor.onChange({
                  identity: {
                    ...editor.value.identity,
                    name: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
        </div>

        <Field label="Short Description">
          <input
            value={editor.value.identity.shortDescription || ""}
            onChange={event =>
              editor.onChange({
                identity: {
                  ...editor.value.identity,
                  shortDescription: event.target.value,
                },
              })
            }
            className={inputClassName()}
          />
        </Field>

        <Field label="Long Description">
          <textarea
            value={editor.value.identity.description || ""}
            onChange={event =>
              editor.onChange({
                identity: {
                  ...editor.value.identity,
                  description: event.target.value,
                },
              })
            }
            rows={5}
            className={inputClassName()}
          />
        </Field>
      </PaneSection>

      <PaneSection label="Lifecycle">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Status">
            <select
              value={editor.value.lifecycle.status}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    status: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Publish Status">
            <select
              value={editor.value.lifecycle.publishStatus}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    publishStatus: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.publishStatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Browse Visibility">
            <select
              value={editor.value.lifecycle.browseVisibility}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    browseVisibility: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.browseVisibilityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </PaneSection>

      <PaneSection label="Classification">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Product Type">
            <input
              value={editor.value.classification.productTypeId || ""}
              onChange={event =>
                editor.onChange({
                  classification: {
                    ...editor.value.classification,
                    productTypeId: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
          <Field label="Service Class">
            <input
              value={editor.value.classification.serviceClass || ""}
              onChange={event =>
                editor.onChange({
                  classification: {
                    ...editor.value.classification,
                    serviceClass: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Service Group">
            <input
              value={editor.value.classification.serviceGroup || ""}
              onChange={event =>
                editor.onChange({
                  classification: {
                    ...editor.value.classification,
                    serviceGroup: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
          <Field label="Default Variant">
            <select
              value={editor.value.defaultVariantId || ""}
              onChange={event => editor.onChange({ defaultVariantId: event.target.value })}
              className={inputClassName()}
            >
              <option value="">No default variant</option>
              {editor.defaultVariantOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </PaneSection>

      <AdvancedSection>
        <Field label="Sort Order" error={editor.validationErrors.sortOrder}>
          <input
            type="number"
            value={editor.value.lifecycle.sortOrder}
            onChange={event =>
              editor.onChange({
                lifecycle: {
                  ...editor.value.lifecycle,
                  sortOrder: Number(event.target.value || 0),
                },
              })
            }
            className={inputClassName()}
          />
        </Field>

        <Field label="Public Facets">
          <div className="space-y-3">
            {editor.value.classification.facets.map((entry, index) => (
              <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-control border border-border/60 p-3 sm:grid-cols-[1fr_1fr_96px_auto]">
                <input
                  value={entry.key}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        facets: editor.value.classification.facets.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, key: event.target.value } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                  placeholder="technology"
                />
                <input
                  value={entry.value}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        facets: editor.value.classification.facets.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, value: event.target.value } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                  placeholder="fiber"
                />
                <input
                  type="number"
                  value={entry.sortOrder}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        facets: editor.value.classification.facets.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, sortOrder: Number(event.target.value || 0) } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                />
                <button
                  type="button"
                  onClick={() =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        facets: editor.value.classification.facets.filter((_, currentIndex) => currentIndex !== index),
                      },
                    })
                  }
                  className="inline-flex h-10 items-center justify-center rounded-control border border-border/70 px-3 text-sm font-semibold text-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                editor.onChange({
                  classification: {
                    ...editor.value.classification,
                    facets: [...editor.value.classification.facets, createClassificationEntry()],
                  },
                })
              }
              className="inline-flex items-center gap-2 rounded-control border border-border/70 px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <Plus className="h-4 w-4" />
              Add facet
            </button>
          </div>
        </Field>

        <Field label="Internal Flags">
          <div className="space-y-3">
            {editor.value.classification.flags.map((entry, index) => (
              <div key={entry.id} className="grid grid-cols-1 gap-3 rounded-control border border-border/60 p-3 sm:grid-cols-[1fr_1fr_96px_auto]">
                <input
                  value={entry.key}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        flags: editor.value.classification.flags.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, key: event.target.value } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                  placeholder="signup_role"
                />
                <input
                  value={entry.value}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        flags: editor.value.classification.flags.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, value: event.target.value } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                  placeholder="plan"
                />
                <input
                  type="number"
                  value={entry.sortOrder}
                  onChange={event =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        flags: editor.value.classification.flags.map((current, currentIndex) =>
                          currentIndex === index ? { ...current, sortOrder: Number(event.target.value || 0) } : current
                        ),
                      },
                    })
                  }
                  className={inputClassName()}
                />
                <button
                  type="button"
                  onClick={() =>
                    editor.onChange({
                      classification: {
                        ...editor.value.classification,
                        flags: editor.value.classification.flags.filter((_, currentIndex) => currentIndex !== index),
                      },
                    })
                  }
                  className="inline-flex h-10 items-center justify-center rounded-control border border-border/70 px-3 text-sm font-semibold text-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                editor.onChange({
                  classification: {
                    ...editor.value.classification,
                    flags: [...editor.value.classification.flags, createClassificationEntry()],
                  },
                })
              }
              className="inline-flex items-center gap-2 rounded-control border border-border/70 px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <Plus className="h-4 w-4" />
              Add flag
            </button>
          </div>
        </Field>
      </AdvancedSection>
    </EditorChrome>
  );
}

export function VariantEditorSheet({ editor }: { editor: VariantEditorViewModel }) {
  if (!editor.isOpen) {
    return null;
  }

  return (
    <EditorChrome
      title={editor.title}
      subtitle={editor.subtitle}
      onClose={editor.onClose}
      footer={
        <EditorFooter
          saveLabel={editor.saveActionLabel}
          saving={editor.state === "saving"}
          onCancel={editor.onClose}
          onSave={() => void editor.onSave()}
        />
      }
    >
      <MetricsSurface metrics={editor.metrics} cols={3} />

      <PaneSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Variant Name" error={editor.validationErrors.name}>
            <input
              value={editor.value.identity.name}
              onChange={event =>
                editor.onChange({
                  identity: {
                    ...editor.value.identity,
                    name: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
        </div>

        <Field label="Display Name">
          <input
            value={editor.value.identity.displayName || ""}
            onChange={event =>
              editor.onChange({
                identity: {
                  ...editor.value.identity,
                  displayName: event.target.value,
                },
              })
            }
            className={inputClassName()}
          />
        </Field>
      </PaneSection>

      <PaneSection label="Lifecycle">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Status">
            <select
              value={editor.value.lifecycle.status}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    status: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sellable">
            <select
              value={editor.value.lifecycle.sellable ? "true" : "false"}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    sellable: event.target.value === "true",
                  },
                })
              }
              className={inputClassName()}
            >
              <option value="true">Sellable</option>
              <option value="false">Not sellable</option>
            </select>
          </Field>
        </div>

      </PaneSection>

      <VariantSpecificationsEditor editor={editor} />

      <PaneSection label="Fulfillment">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Installation Policy">
            <select
              value={editor.value.fulfillment.installationPolicy}
              onChange={event =>
                editor.onChange({
                  fulfillment: {
                    ...editor.value.fulfillment,
                    installationPolicy: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.installationPolicyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </PaneSection>

      <PaneSection label="Technical Attributes">
        {!editor.value.attributes.some(attribute => attribute.attributeKey === 'broadband.consumer_label') && editor.attributeDefinitions.some(definition => definition.attributeKey === 'broadband.consumer_label') ? (
          <button type="button" onClick={() => editor.onAddAttribute('broadband.consumer_label')} className="mb-3 min-h-11 rounded-control border border-muted-foreground! px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">Configure Broadband Facts</button>
        ) : null}
        <DescriptionText>
          Technical attribute values drive storefront comparison features and signup grouping logic.
        </DescriptionText>

        <VerticalStack gap={3}>
          {editor.value.attributes.length === 0 ? (
            <Surface className="border-border/60 bg-muted/10 p-4">
              <VerticalStack gap={4}>
                <DescriptionText className="text-xs">
                  No technical attributes defined for this variant.
                </DescriptionText>
                <button
                  type="button"
                  onClick={() => editor.onAddAttribute()}
                  className="inline-flex w-fit items-center gap-2 rounded-control border border-border/70 px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <Plus className="h-4 w-4" />
                  Add attribute
                </button>
              </VerticalStack>
            </Surface>
          ) : (
            <>
              {editor.value.attributes.map(attribute => {
                const attributeDefinition = editor.attributeDefinitions.find(
                  def => def.attributeKey === attribute.attributeKey
                );
                const isBandwidth = attributeDefinition?.quantityDimension === "bandwidth";
                const isBoolean = attributeDefinition?.valueType === "boolean";
                const isObject = attributeDefinition?.valueType === "object";
                const isNumeric = attributeDefinition?.valueType === "integer" || attributeDefinition?.valueType === "decimal";

                const definitionError = editor.validationErrors[`attribute.${attribute.id}.definition`];
                const valueError = editor.validationErrors[`attribute.${attribute.id}.value`];

                return (
                  <div
                    key={attribute.id}
                    className={`grid grid-cols-1 gap-3 rounded-control border border-border/60 p-3 ${
                      attribute.attributeKey === "broadband.consumer_label" ? "sm:grid-cols-1" : isObject ? "sm:grid-cols-[minmax(0,1fr)_2fr_auto]" : "sm:grid-cols-[1fr_1fr_auto]"
                    }`}
                  >
                    <Field label="Definition" error={definitionError}>
                      <select
                        value={attribute.attributeKey}
                        onChange={event =>
                          editor.onUpdateAttribute(attribute.id, {
                            attributeKey: event.target.value,
                          })
                        }
                        className={inputClassName()}
                      >
                        <option value="">Select definition</option>
                        {editor.attributeDefinitions.map(def => (
                          <option
                            key={def.attributeKey}
                            value={def.attributeKey}
                            disabled={
                              editor.value.attributes.some(
                                a => a.attributeKey === def.attributeKey && a.id !== attribute.id
                              )
                            }
                          >
                            {def.displayName}
                          </option>
                        ))}
                      </select>
                    </Field>

                    {attribute.attributeKey ? (
                      <Field label="Value" error={attribute.attributeKey === 'broadband.consumer_label' ? undefined : valueError}>
                        <HorizontalStack gap={3}>
                          {attribute.attributeKey === 'broadband.consumer_label' ? (
                            <BroadbandFactsEditor error={valueError} value={attribute.value} onChange={value => editor.onUpdateAttribute(attribute.id, { value })} />
                          ) : isObject ? (
                            <textarea
                              rows={10}
                              spellCheck={false}
                              aria-label={`${attributeDefinition?.displayName || "Attribute"} JSON object`}
                              value={attribute.value}
                              onChange={event =>
                                editor.onUpdateAttribute(attribute.id, {
                                  value: event.target.value,
                                })
                              }
                              className={`${inputClassName()} min-h-40 resize-y font-mono text-xs`}
                            />
                          ) : isBoolean ? (
                            <select
                              value={attribute.value}
                              onChange={event =>
                                editor.onUpdateAttribute(attribute.id, {
                                  value: event.target.value,
                                })
                              }
                              className={inputClassName()}
                            >
                              <option value="">Select value</option>
                              <option value="true">True</option>
                              <option value="false">False</option>
                            </select>
                          ) : isNumeric || isBandwidth ? (
                            <input
                              type="number"
                              step={attributeDefinition?.valueType === "integer" && !isBandwidth ? "1" : "any"}
                              value={attribute.value}
                              onChange={event =>
                                editor.onUpdateAttribute(attribute.id, {
                                  value: event.target.value,
                                })
                              }
                              className={inputClassName()}
                            />
                          ) : (
                            <input
                              type="text"
                              value={attribute.value}
                              onChange={event =>
                                editor.onUpdateAttribute(attribute.id, {
                                  value: event.target.value,
                                })
                              }
                              className={inputClassName()}
                            />
                          )}

                          {isBandwidth && (
                            <select
                              value={attribute.unit}
                              onChange={event =>
                                editor.onUpdateAttribute(attribute.id, {
                                  unit: event.target.value as "Mbps" | "Gbps",
                                })
                              }
                              className={inputClassName()}
                            >
                              <option value="Mbps">Mbps</option>
                              <option value="Gbps">Gbps</option>
                            </select>
                          )}
                        </HorizontalStack>
                      </Field>
                    ) : (
                      <Field label="Value">
                        <input value="" disabled className={inputClassName()} />
                      </Field>
                    )}

                    <div className="flex h-full items-end pb-1">
                      <button
                        type="button"
                        onClick={() => editor.onRemoveAttribute(attribute.id)}
                        aria-label={`Remove ${attributeDefinition?.displayName || "attribute"}`}
                        className="inline-flex h-10 w-full items-center justify-center rounded-control border border-border/70 px-3 text-sm font-semibold text-foreground transition-colors hover:border-destructive/40 hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="ml-2 sm:sr-only">Remove</span>
                      </button>
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => editor.onAddAttribute()}
                className="inline-flex w-fit items-center gap-2 rounded-control border border-border/70 px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                <Plus className="h-4 w-4" />
                Add attribute
              </button>
            </>
          )}
        </VerticalStack>
      </PaneSection>

      <PaneSection label="Option Values">
        {editor.optionValueOptions.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {editor.optionValueOptions.map(option => {
              const checked = editor.value.optionValueIds.includes(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-muted/10 p-3 transition-all hover:bg-muted/20"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={event => {
                      const next = event.target.checked
                        ? [...editor.value.optionValueIds, option.value]
                        : editor.value.optionValueIds.filter(value => value !== option.value);
                      editor.onChange({ optionValueIds: next });
                    }}
                    className="mt-0.5"
                  />
                  <VerticalStack gap={1}>
                    <p className="text-sm font-bold text-foreground">{option.label}</p>
                    {option.description ? (
                      <DescriptionText className="text-[11px]">{option.description}</DescriptionText>
                    ) : null}
                  </VerticalStack>
                </label>
              );
            })}
          </div>
        ) : (
          <Surface className="border-border/60 bg-muted/10 p-4">
            <DescriptionText className="text-xs">
              No option values are configured for this product yet. Variants can still be authored without option bindings.
            </DescriptionText>
          </Surface>
        )}
      </PaneSection>

      <AdvancedSection>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Fulfillment Code">
            <input
              value={editor.value.fulfillment.fulfillmentCode || ""}
              onChange={event =>
                editor.onChange({
                  fulfillment: {
                    ...editor.value.fulfillment,
                    fulfillmentCode: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
          <Field label="Inventory Policy">
            <select
              value={editor.value.fulfillment.inventoryPolicy || ""}
              onChange={event =>
                editor.onChange({
                  fulfillment: {
                    ...editor.value.fulfillment,
                    inventoryPolicy: event.target.value,
                  },
                })
              }
              className={inputClassName()}
            >
              {editor.inventoryPolicyOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sort Order" error={editor.validationErrors.sortOrder}>
            <input
              type="number"
              value={editor.value.lifecycle.sortOrder}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    sortOrder: Number(event.target.value || 0),
                  },
                })
              }
              className={inputClassName()}
            />
          </Field>
        </div>
      </AdvancedSection>
    </EditorChrome>
  );
}

export function VariantOfferEditorSheet({ editor }: { editor: VariantOfferEditorViewModel }) {
  if (!editor.isOpen) {
    return null;
  }

  const isEquipmentLeaseOffer = editor.value.commercial.offerType === "equipment_lease_recurring";

  return (
    <EditorChrome
      title={editor.title}
      subtitle={editor.subtitle}
      onClose={editor.onClose}
      footer={
        <EditorFooter
          saveLabel={editor.saveActionLabel}
          saving={editor.state === "saving"}
          onCancel={editor.onClose}
          onSave={() => void editor.onSave()}
        />
      }
    >
      <MetricsSurface metrics={editor.metrics} cols={3} />

      <PaneSection label="Identity">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Offer Name" error={editor.validationErrors.name}>
            <input aria-label="Offer Name"
              value={editor.value.identity.name}
              onChange={event =>
                editor.onChange({
                  identity: {
                    ...editor.value.identity,
                    name: event.target.value,
                  },
                })
              }
              className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
            />
          </Field>
        </div>

        <Field label="Display Name">
          <input aria-label="Display Name"
            value={editor.value.identity.displayName || ""}
            onChange={event =>
              editor.onChange({
                identity: {
                  ...editor.value.identity,
                  displayName: event.target.value,
                },
              })
            }
            className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
          />
        </Field>
      </PaneSection>

      <PaneSection label="Commercial Terms">
        <div className="grid grid-cols-1 gap-4">
          <Field label="Offer Type" error={editor.validationErrors.offerType}>
            <select aria-label="Offer Type"
              value={editor.value.commercial.offerType}
              onChange={event =>
                editor.onChange({
                  commercial: {
                    ...editor.value.commercial,
                    offerType: event.target.value,
                    billingUnit: event.target.value === "equipment_lease_recurring" ? "months" : editor.value.commercial.billingUnit,
                    billingPeriodMonths: ['purchase', 'customer_owned'].includes(event.target.value)
                      ? null : event.target.value === 'equipment_lease_recurring' ? 1 : editor.value.commercial.billingPeriodMonths,
                  },
                })
              }
              className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
            >
              {editor.offerTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {!['purchase', 'customer_owned'].includes(editor.value.commercial.offerType) && (
          <Field label="Billing frequency" error={editor.validationErrors.billingPeriodMonths}>
            <div className="grid grid-cols-2 gap-3">
              <input aria-label="Billing frequency value" type="number" min="1" step="1"
                disabled={isEquipmentLeaseOffer}
                value={editor.value.commercial.billingPeriodMonths == null ? "" : editor.value.commercial.billingPeriodMonths / (editor.value.commercial.billingUnit === "years" ? 12 : 1)}
                onChange={event => editor.onChange({commercial: {...editor.value.commercial,
                  billingPeriodMonths: event.target.value === "" ? null : Number(event.target.value) * (editor.value.commercial.billingUnit === "years" ? 12 : 1)}})}
                className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")} />
              <select aria-label="Billing frequency unit" disabled={isEquipmentLeaseOffer}
                value={editor.value.commercial.billingUnit ?? "months"}
                onChange={event => editor.onChange({commercial: {...editor.value.commercial,
                  billingUnit: event.target.value as "months" | "years",
                  billingPeriodMonths: editor.value.commercial.billingPeriodMonths == null ? null : editor.value.commercial.billingPeriodMonths / (editor.value.commercial.billingUnit === "years" ? 12 : 1) * (event.target.value === "years" ? 12 : 1)}})}
                className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}>
                <option value="months">Months</option><option value="years">Years</option>
              </select>
            </div>
            <p className="text-sm text-muted-foreground">Bill every selected period. The pricebook amount is the price per bill.</p>
          </Field>
        )}
        {!isEquipmentLeaseOffer && (
          <Field label="Contract duration" error={editor.validationErrors.termMonths}>
            <div className="grid grid-cols-2 gap-3">
              <input aria-label="Contract duration value" type="number" min="1" step="1"
                placeholder="No fixed term"
                value={editor.value.commercial.termMonths == null ? "" : editor.value.commercial.termMonths / (editor.value.commercial.termUnit === "years" ? 12 : 1)}
                onChange={event => editor.onChange({commercial: {...editor.value.commercial,
                  termMonths: event.target.value === "" ? null : Number(event.target.value) * (editor.value.commercial.termUnit === "years" ? 12 : 1)}})}
                className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")} />
              <select aria-label="Contract duration unit" value={editor.value.commercial.termUnit ?? "months"}
                onChange={event => editor.onChange({commercial: {...editor.value.commercial,
                  termUnit: event.target.value as "months" | "years",
                  termMonths: editor.value.commercial.termMonths == null ? null : editor.value.commercial.termMonths / (editor.value.commercial.termUnit === "years" ? 12 : 1) * (event.target.value === "years" ? 12 : 1)}})}
                className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}>
                <option value="months">Months</option><option value="years">Years</option>
              </select>
            </div>
            <p className="text-sm text-muted-foreground">Leave empty for no fixed term. Duration is independent of how often the customer is billed.</p>
          </Field>
        )}
      </PaneSection>

      <PaneSection label="Lifecycle">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Status">
            <select aria-label="Status"
              value={editor.value.lifecycle.status}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    status: event.target.value,
                  },
                })
              }
              className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
            >
              {editor.statusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Behavior">
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={editor.value.lifecycle.sellable}
                  onChange={event =>
                    editor.onChange({
                      lifecycle: {
                        ...editor.value.lifecycle,
                        sellable: event.target.checked,
                      },
                    })
                  }
                />
                Sellable
              </label>
              <label className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={editor.value.lifecycle.isDefault}
                  onChange={event =>
                    editor.onChange({
                      lifecycle: {
                        ...editor.value.lifecycle,
                        isDefault: event.target.checked,
                      },
                    })
                  }
                />
                Default
              </label>
            </div>
          </Field>
        </div>
      </PaneSection>

      <AdvancedSection>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Fulfillment Code">
            <input
              value={editor.value.commercial.fulfillmentCode || ""}
              onChange={event =>
                editor.onChange({
                  commercial: {
                    ...editor.value.commercial,
                    fulfillmentCode: event.target.value,
                  },
                })
              }
              className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
            />
          </Field>
          <Field label="Sort Order" error={editor.validationErrors.sortOrder}>
            <input
              type="number"
              value={editor.value.lifecycle.sortOrder}
              onChange={event =>
                editor.onChange({
                  lifecycle: {
                    ...editor.value.lifecycle,
                    sortOrder: Number(event.target.value || 0),
                  },
                })
              }
              className={inputClassName().replace("border-border/60", "border-muted-foreground").replace("focus:border-primary/40", "focus:border-primary")}
            />
          </Field>
        </div>
      </AdvancedSection>
    </EditorChrome>
  );
}
